import { useState, useEffect, useMemo } from "react";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import * as services from "../../../services/saleng";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { getAdjustedPurchaseItems } from "../../../utils/adjustPurchaseItems";

export default function SummaryTicketPurchases() { // Renamed to match file purpose
  const formatDate = (date) => date.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return formatDate(date);
  });
  const [endDate, setEndDate] = useState(formatDate(new Date()));
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [openSet, setOpenSet] = useState(() => new Set());
  const [search, setSearch] = useState("");

  const [orderBy, setOrderBy] = useState("createdAt");
  const [order, setOrder] = useState("desc");

  // ---------- Fetch: ใช้ getPurchaseTransactions ----------
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await services.getPurchaseTransactions({
          status: "payed",
          startDate,
          endDate,
          dateField: "paidAt",
        });

        const rawData = res?.data?.data ?? res?.data ?? res ?? [];

        const processed = (Array.isArray(rawData) ? rawData : [rawData]).map(
          (row) => {
            const items = getAdjustedPurchaseItems(row).map((item) => ({
              ...item,
              productName: item.productName ?? item.product?.title ?? "-",
              unit: item.unit ?? "กก.",
            }));

            return { ...row, items };
          }
        );

        setRows(processed);
        setOpenSet(new Set());
      } catch (err) {
        console.error("fetch error", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate]);

  // ---------- Utils ----------
  const formatNum = (val) =>
    val != null
      ? Number(val).toLocaleString("th-TH", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "-";

  const formatDateTime = (datetimeStr) => {
    const date = new Date(datetimeStr);
    return date.toLocaleDateString("th-TH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatThaiDate = (datetimeStr) => {
    const date = new Date(datetimeStr);
    return date.toLocaleDateString("th-TH", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getRowMetrics = (row) => {
    const totalWeight =
      row.items?.reduce((acc, i) => acc + (i.netWeight || 0), 0) ?? 0;
    const beforeVat =
      row.items?.reduce((acc, i) => acc + (i.amountBeforeVat || 0), 0) ?? 0;
    const vat = row.items?.reduce((acc, i) => acc + (i.vat || 0), 0) ?? 0;
    const totalNet =
      row.items?.reduce((acc, i) => acc + (i.netPrice || 0), 0) ?? 0;
    const avgUnit =
      row.items?.length > 0
        ? row.items.reduce((acc, i) => acc + (i.pricePerUnit || 0), 0) /
          row.items.length
        : 0;
    return { totalWeight, beforeVat, vat, totalNet, avgUnit };
  };

  // ---------- Search ----------
  const filteredRows = useMemo(() => {
    const q = (search || "").toLowerCase().trim();
    if (!q) return rows;

    return rows.filter((row) => {
      const bill = (row.billNumber || "").toLowerCase();
      const customer =
        row.customer?.name?.toLowerCase() ||
        row.customerName?.toLowerCase() ||
        "";
      const dateTH = formatThaiDate(row.createdAt).toLowerCase();
      const dateISO = String(row.createdAt || "").toLowerCase();
      const productList = (row.items || [])
        .map((it) => (it.productName || "").toLowerCase())
        .join(" ");
      const car = `${row.car?.type || ""} ${row.car?.license || ""}`
        .trim()
        .toLowerCase();

      return (
        bill.includes(q) ||
        customer.includes(q) ||
        dateTH.includes(q) ||
        dateISO.includes(q) ||
        productList.includes(q) ||
        car.includes(q)
      );
    });
  }, [rows, search]);

  // ---------- Sort ----------
  const sortedRows = useMemo(() => {
    const data = [...filteredRows];
    data.sort((a, b) => {
      const mA = getRowMetrics(a);
      const mB = getRowMetrics(b);

      let aVal;
      let bVal;

      switch (orderBy) {
        case "billNumber":
          aVal = a.billNumber ?? "";
          bVal = b.billNumber ?? "";
          break;
        case "createdAt":
          aVal = new Date(a.createdAt).getTime() || 0;
          bVal = new Date(b.createdAt).getTime() || 0;
          break;
        case "paidAt":
          aVal = new Date(a.paidAt).getTime() || 0;
          bVal = new Date(b.paidAt).getTime() || 0;
          break;
        case "customerName":
          aVal = (a.customer?.name ?? a.customerName ?? "").toLowerCase();
          bVal = (b.customer?.name ?? b.customerName ?? "").toLowerCase();
          break;
        case "totalWeight":
          aVal = mA.totalWeight;
          bVal = mB.totalWeight;
          break;
        case "beforeVat":
          aVal = mA.beforeVat;
          bVal = mB.beforeVat;
          break;
        case "vat":
          aVal = mA.vat;
          bVal = mB.vat;
          break;
        case "totalNet":
          aVal = mA.totalNet;
          bVal = mB.totalNet;
          break;
        default:
          aVal = 0;
          bVal = 0;
      }

      if (typeof aVal === "string" && typeof bVal === "string") {
        const cmp = aVal.localeCompare(bVal, "th");
        return order === "asc" ? cmp : -cmp;
      } else {
        const cmp = (aVal || 0) - (bVal || 0);
        return order === "asc" ? cmp : -cmp;
      }
    });
    return data;
  }, [filteredRows, orderBy, order]);

  const requestSort = (key) => {
    if (orderBy === key) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(key);
      if (["billNumber", "customerName"].includes(key)) setOrder("asc");
      else setOrder("desc");
    }
  };

  const getRowKey = (row, idx) =>
    String(row.transactionId ?? row.id ?? row.billNumber ?? idx);

  const toggleOpen = (key) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ---------- Export CSV ----------
  function exportToCSV(data, filename = "purchase-summary.csv") {
    if (!data || data.length === 0) return;
    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((field) => `"${row[field] ?? ""}"`).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const handleExportMain = () => {
    const dataset = sortedRows.map((row) => {
      const { totalWeight, beforeVat, vat, totalNet, avgUnit } =
        getRowMetrics(row);

      return {
        เลขที่ตั๋ว: row.billNumber ?? "",
        วันที่จ่าย: formatDateTime(row.createdAt),
        ชื่อลูกค้า: row.customer?.name ?? row.customerName ?? "-",
        รถขนส่ง: row.car?.license
          ? `${row.car?.type || ""} ${row.car.license}`.trim()
          : "-",
        ประเภทลูกค้า:
          row.customer?.personType === "company"
            ? "นิติบุคคล"
            : row.customer?.personType === "normal"
              ? "บุคคลธรรมดา"
              : "-",
        "น้ำหนักรวม (กก.)": Number(totalWeight).toFixed(2),
        หน่วย: "กก.",
        "ราคาซื้อ/หน่วย": Number(avgUnit).toFixed(2),
        ยอดก่อนภาษี: Number(beforeVat).toFixed(2),
        ภาษี: Number(vat).toFixed(2),
        ยอดรวม: Number(totalNet).toFixed(2),
        ช่องทางจ่ายเงิน: row.paymentMethod ?? "-",
        ชื่อบัญชี: row.paymentMethod === "โอนเงิน" ? row.customer?.bankAccountName ?? ""  : "",
        ธนาคาร: row.paymentMethod === "โอนเงิน" ? row.customer?.bankName ?? ""  : "",
        เลขที่บัญชี: row.paymentMethod === "โอนเงิน" ? row.customer?.bankAccountNumber ?? ""  : "",

      };
    });

    exportToCSV(dataset, `purchase_tickets_${startDate}_to_${endDate}.csv`);
  };

  const handleExportRowItems = (row) => {
    const dataset = (row.items || []).map((it) => ({
      สินค้า: it.productName ?? "-",
      "น้ำหนัก (กก.)": Number(it.netWeight || 0).toFixed(2),
      หน่วย: it.unit ?? "กก.",
      "ราคาซื้อ/หน่วย": Number(it.pricePerUnit || 0).toFixed(2),
      ยอดก่อนภาษี: Number(it.amountBeforeVat || 0).toFixed(2),
      ภาษี: Number(it.vat || 0).toFixed(2),
      ยอดรวม: Number(it.netPrice || 0).toFixed(2),
    }));

    const fname = `ticket_${row.billNumber || row.id || "detail"}.csv`;
    exportToCSV(dataset, fname);
  };

  const handleExportAllItems = () => {
    const dataset = [];

    sortedRows.forEach((row) => {
      const common = {
        เลขที่ตั๋ว: row.billNumber ?? "",
        วันที่จ่าย: formatDateTime(row.createdAt),
        ชื่อลูกค้า: row.customer?.name ?? row.customerName ?? "-",
        รถขนส่ง: row.car?.license
          ? `${row.car?.type || ""} ${row.car.license}`.trim()
          : "-",
        ประเภทลูกค้า:
          row.customer?.personType === "company"
            ? "นิติบุคคล"
            : row.customer?.personType === "normal"
              ? "บุคคลธรรมดา"
              : "-",
        ช่องทางจ่ายเงิน: row.paymentMethod ?? "-",
  
      };

      (row.items || []).forEach((it) => {
        dataset.push({
          ...common,
          สินค้า: it.productName ?? "-",
          "น้ำหนัก (กก.)": Number(it.netWeight || 0).toFixed(2),
          หน่วย: it.unit ?? "กก.",
          "ราคาซื้อ/หน่วย": Number(it.pricePerUnit || 0).toFixed(2),
          ยอดก่อนภาษี: Number(it.amountBeforeVat || 0).toFixed(2),
          ภาษี: Number(it.vat || 0).toFixed(2),
          ยอดรวม: Number(it.netPrice || 0).toFixed(2),
        });
      });
    });

    if (dataset.length === 0) return;
    exportToCSV(dataset, `purchase_items_${startDate}_to_${endDate}.csv`);
  };

  // ---------- Summary ----------
  const summary = useMemo(() => {
    let ticketCount = sortedRows.length;
    let totalWeight = 0;
    let totalBeforeVat = 0;
    let totalVat = 0;
    let totalNet = 0;

    sortedRows.forEach((row) => {
      const m = getRowMetrics(row);
      totalWeight += m.totalWeight;
      totalBeforeVat += m.beforeVat;
      totalVat += m.vat;
      totalNet += m.totalNet;
    });

    return {
      ticketCount,
      totalWeight,
      totalBeforeVat,
      totalVat,
      totalNet,
    };
  }, [sortedRows]);

  return (
    <div className="min-h-screen bg-gray-50 p-4 font-sans">
      <div className="max-w-[95%] mx-auto">
        <div className="mb-6">
          <AppBreadcrumbs />
        </div>

         {/* Header & Controls */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <span className="w-1 h-8 bg-green-600 rounded-full"></span>
              สรุปยอดซื้อสินค้ารายตั๋ว
            </h1>

            <div className="flex flex-wrap items-center gap-3">
               {/* Search */}
               <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                    type="text"
                    placeholder="ค้นหา (เลขตั๋ว/ลูกค้า/รถ...)"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all w-64"
                />
               </div>

              {/* Date Pickers */}
              <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200">
                <span className="text-sm font-medium text-gray-500 whitespace-nowrap">เริ่ม</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-transparent border-none focus:ring-0 text-gray-700 font-medium text-sm"
                />
              </div>

               <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-200">
                <span className="text-sm font-medium text-gray-500 whitespace-nowrap">สิ้นสุด</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-transparent border-none focus:ring-0 text-gray-700 font-medium text-sm"
                />
              </div>
            </div>
          </div>
          
           <div className="mt-6 flex gap-2 justify-end">
                <button
                    onClick={handleExportMain}
                    className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all font-medium shadow-sm"
                >
                    <ContentCopyIcon fontSize="small" />
                    <span>Export ข้อมูลหลัก</span>
                </button>
                 <button
                    onClick={handleExportAllItems}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white border border-transparent rounded-xl hover:bg-green-700 transition-all font-medium shadow-sm hover:shadow-md"
                >
                    <ContentCopyIcon fontSize="small" />
                    <span>Export ข้อมูลย่อยทั้งหมด</span>
                </button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
             <div className="flex justify-center items-center h-64">
                <div className="w-12 h-12 border-4 border-green-200 border-t-green-600 rounded-full animate-spin"></div>
             </div>
        ) : (
             <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-100">
                             <tr>
                                <th className="px-4 py-4 w-12"></th>
                                <th onClick={() => requestSort("billNumber")} className="px-4 py-4 cursor-pointer hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center gap-1">
                                        เลขที่ตั๋ว {orderBy === "billNumber" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => requestSort("createdAt")} className="px-4 py-4 cursor-pointer hover:bg-gray-100 transition-colors">
                                     <div className="flex items-center gap-1">
                                        วันที่เปิดตั๋ว {orderBy === "createdAt" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => requestSort("paidAt")} className="px-4 py-4 cursor-pointer hover:bg-gray-100 transition-colors">
                                     <div className="flex items-center gap-1">
                                        วันที่จ่าย {orderBy === "paidAt" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => requestSort("customerName")} className="px-4 py-4 cursor-pointer hover:bg-gray-100 transition-colors">
                                     <div className="flex items-center gap-1">
                                        ชื่อลูกค้า {orderBy === "customerName" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th className="px-4 py-4">รถขนส่ง</th>
                                <th className="px-4 py-4">ประเภทลูกค้า</th>
                                <th onClick={() => requestSort("totalWeight")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors">
                                     <div className="flex items-center justify-end gap-1">
                                        น้ำหนักรวม {orderBy === "totalWeight" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th className="px-4 py-4">หน่วย</th>
                                <th className="px-4 py-4 text-right">ราคาซื้อ/หน่วย</th>
                                <th onClick={() => requestSort("beforeVat")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center justify-end gap-1">
                                        ยอดก่อนภาษี {orderBy === "beforeVat" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => requestSort("vat")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center justify-end gap-1">
                                        ภาษี {orderBy === "vat" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th onClick={() => requestSort("totalNet")} className="px-4 py-4 text-right cursor-pointer hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center justify-end gap-1">
                                        ยอดรวม {orderBy === "totalNet" && <span className="text-green-600">{order === "asc" ? "↑" : "↓"}</span>}
                                    </div>
                                </th>
                                <th className="px-4 py-4">ช่องทาง</th>
                                <th className="px-4 py-4">ชื่อบัญชี</th>
                                <th className="px-4 py-4">ธนาคาร</th>
                                <th className="px-4 py-4">เลขที่บัญชี</th>
                             </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {sortedRows.map((row, idx) => {
                                const key = getRowKey(row, idx);
                                const isOpen = openSet.has(key);
                                const { totalWeight, beforeVat, vat, totalNet, avgUnit } = getRowMetrics(row);
                                return (
                                    <RowGroup
                                        key={key}
                                        isOpen={isOpen}
                                        onToggle={() => toggleOpen(key)}
                                        row={row}
                                        metrics={{ totalWeight, beforeVat, vat, totalNet, avgUnit }}
                                        formatNum={formatNum}
                                        formatDateTime={formatDateTime}
                                        handleExportRowItems={handleExportRowItems}
                                    />
                                );
                            })}
                        </tbody>
                        <tfoot className="bg-gray-50 font-bold text-gray-900 border-t border-gray-200">
                             <tr>
                                <td colSpan={6} className="px-4 py-4 text-right">
                                    ยอดรวม {summary.ticketCount.toLocaleString("th-TH")} ตั๋ว
                                </td>
                                <td></td>
                                <td className="px-4 py-4 text-right">{formatNum(summary.totalWeight)}</td>
                                <td></td>
                                <td></td>
                                <td className="px-4 py-4 text-right">{formatNum(summary.totalBeforeVat)}</td>
                                <td className="px-4 py-4 text-right">{formatNum(summary.totalVat)}</td>
                                <td className="px-4 py-4 text-right text-green-700 text-lg">{formatNum(summary.totalNet)}</td>
                                <td colSpan={4}></td>
                             </tr>
                        </tfoot>
                    </table>
                </div>
             </div>
        )}
      </div>
    </div>
  );
}

function RowGroup({ isOpen, onToggle, row, metrics, formatNum, formatDateTime, handleExportRowItems }) {
    return (
        <>
            <tr className="hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-none">
                 <td className="px-4 py-4">
                    <button 
                        onClick={onToggle}
                        className="p-1 rounded-full hover:bg-gray-200 transition-colors text-gray-500"
                    >
                        {isOpen ? <KeyboardArrowDownIcon fontSize="small"/> : <KeyboardArrowRightIcon fontSize="small"/>}
                    </button>
                </td>
                <td className="px-4 py-4 font-medium text-gray-800">{row.billNumber}</td>
                <td className="px-4 py-4 text-gray-600">{formatDateTime(row.createdAt)}</td>
                <td className="px-4 py-4 text-gray-600">{formatDateTime(row.paidAt)}</td>
                <td className="px-4 py-4 font-medium text-gray-800">{row.customer?.name ?? row.customerName ?? "-"}</td>
                <td className="px-4 py-4">
                     <span className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-600">
                        {row.car?.license ? `${row.car?.type || ""} ${row.car.license}`.trim() : "-"}
                    </span>
                </td>
                 <td className="px-4 py-4 text-gray-600">
                    {row.customer?.personType === "company"
                        ? "นิติบุคคล"
                        : row.customer?.personType === "normal"
                        ? "บุคคลธรรมดา"
                        : "-"}
                </td>
                <td className="px-4 py-4 text-right font-medium">{formatNum(metrics.totalWeight)}</td>
                <td className="px-4 py-4 text-gray-500 text-sm">กก.</td>
                <td className="px-4 py-4 text-right text-gray-600">{formatNum(metrics.avgUnit)}</td>
                <td className="px-4 py-4 text-right text-gray-600">{formatNum(metrics.beforeVat)}</td>
                <td className="px-4 py-4 text-right text-gray-600">{formatNum(metrics.vat)}</td>
                <td className="px-4 py-4 text-right font-bold text-gray-800">{formatNum(metrics.totalNet)}</td>
                <td className="px-4 py-4 text-gray-600 text-sm">{row.paymentMethod ?? "-"}</td>
                <td className="px-4 py-4 text-gray-500 text-xs">{(row.paymentMethod === "โอนเงิน" && row.customer?.bankAccountName) ?? ""}</td>
                <td className="px-4 py-4 text-gray-500 text-xs">{(row.paymentMethod === "โอนเงิน" && row.customer?.bankName) ?? ""}</td>
                <td className="px-4 py-4 text-gray-500 text-xs">{(row.paymentMethod === "โอนเงิน" && row.customer?.bankAccountNumber) ?? ""}</td>
            </tr>

            {isOpen && (
                <tr className="bg-gray-50/50">
                    <td colSpan={17} className="px-4 py-4">
                        <div className="pl-12 pr-4">
                            <div className="flex justify-between items-center mb-3">
                                <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2">
                                    <span className="w-1 h-4 bg-green-500 rounded-full"></span>
                                    รายการสินค้า
                                </h4>
                                <button
                                    onClick={() => handleExportRowItems(row)}
                                    className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 hover:underline border border-green-200 bg-white px-2 py-1 rounded"
                                >
                                    <ContentCopyIcon style={{ fontSize: 14 }} />
                                    Export ข้อมูลย่อย
                                </button>
                            </div>
                            
                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                <table className="w-full text-sm text-left">
                                    <thead className="bg-gray-50 text-gray-500 border-b border-gray-200">
                                        <tr>
                                            <th className="px-4 py-2 font-medium">สินค้า</th>
                                            <th className="px-4 py-2 text-right font-medium">น้ำหนัก</th>
                                            <th className="px-4 py-2 font-medium">หน่วย</th>
                                            <th className="px-4 py-2 text-right font-medium">ราคาซื้อ/หน่วย</th>
                                            <th className="px-4 py-2 text-right font-medium">ยอดก่อนภาษี</th>
                                            <th className="px-4 py-2 text-right font-medium">ภาษี</th>
                                            <th className="px-4 py-2 text-right font-medium">ยอดรวม</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {(row.items || []).map((p, i) => (
                                            <tr key={i} className="hover:bg-gray-50">
                                                <td className="px-4 py-2 text-gray-800">{p.productName ?? "-"}</td>
                                                <td className="px-4 py-2 text-right text-gray-600">{formatNum(p.netWeight)}</td>
                                                <td className="px-4 py-2 text-gray-500">{p.unit ?? "กก."}</td>
                                                <td className="px-4 py-2 text-right text-gray-600">{formatNum(p.pricePerUnit)}</td>
                                                <td className="px-4 py-2 text-right text-gray-600">{formatNum(p.amountBeforeVat)}</td>
                                                <td className="px-4 py-2 text-right text-gray-600">{formatNum(p.vat)}</td>
                                                <td className="px-4 py-2 text-right font-medium text-gray-800">{formatNum(p.netPrice)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}
